import React from 'react';

import { createFacetIcon, FACET_TONES } from './facet';

const buildAchievementKey = (typeId, value) => {
  const safeType = Number(typeId);
  const safeValue = value === null || typeof value === 'undefined' ? 'null' : Number(value);
  return `${safeType}:${safeValue}`;
};

const normalizeAchievementName = (name) =>
  typeof name === 'string' ? name.trim().toLowerCase() : '';

export const ACHIEVEMENT_ICON_STATES = {
  earned: 'earned',
  normal: 'normal',
};

export const InlineAchievementIcon = ({ className, html }) => {
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (containerRef.current) {
      containerRef.current.innerHTML = html || '';
    }
  }, [html]);

  return <div className={className} ref={containerRef} aria-hidden="true" />;
};

// Tiered families encode their count as geometry (see facet/glyphs.js) and
// close their progress ring on a log scale across the family, so early tiers
// stay visible against the top-tier ceiling.
const SESSION_TIERS = [1, 5, 10, 25, 50, 100, 200, 500, 1000, 2500, 5000, 10000];
const WEEK_STREAK_TIERS = [1, 2, 3, 5, 10, 20, 30, 50, 100, 200, 300, 500];

const tierRing = (value, maxValue) => Math.log(value) / Math.log(maxValue);

const alphaPosseIcon = createFacetIcon('apMember', { tone: FACET_TONES.milestones });
const trainingLogIcon = createFacetIcon('chevrons', { tone: FACET_TONES.milestones });
const profileCompletedIcon = createFacetIcon('person', { tone: FACET_TONES.milestones });
const firstStepsIcon = createFacetIcon('arrow', { tone: FACET_TONES.milestones });
const welcomeIcon = createFacetIcon('check', { tone: FACET_TONES.milestones });
const anniversaryIcon = createFacetIcon('burst', { rare: true });
// Membership badges (type 10), drawn from the brand icons.
const apprenticeIcon = createFacetIcon('apprentice', { tone: FACET_TONES.milestones });
const trainerIcon = createFacetIcon('trainer', { tone: FACET_TONES.milestones });
const allInIcon = createFacetIcon('allIn', { rare: true });

const achievementIconRegistry = {
  [buildAchievementKey(4, 3)]: alphaPosseIcon,
  [buildAchievementKey(4, 1)]: trainingLogIcon,
  [buildAchievementKey(4, 4)]: profileCompletedIcon,
  [buildAchievementKey(5, null)]: profileCompletedIcon,
  [buildAchievementKey(9, null)]: anniversaryIcon,
};

SESSION_TIERS.forEach((value) => {
  achievementIconRegistry[buildAchievementKey(1, value)] = createFacetIcon(`s${value}`, {
    tone: FACET_TONES.sessions,
    tiered: true,
    tier: tierRing(value, SESSION_TIERS[SESSION_TIERS.length - 1]),
  });
});

WEEK_STREAK_TIERS.forEach((value) => {
  achievementIconRegistry[buildAchievementKey(2, value)] = createFacetIcon(`w${value}`, {
    tone: FACET_TONES.weekStreaks,
    tiered: true,
    tier: tierRing(value, WEEK_STREAK_TIERS[WEEK_STREAK_TIERS.length - 1]),
  });
});

const achievementIconByName = {
  [normalizeAchievementName('AP Member')]: alphaPosseIcon,
  [normalizeAchievementName('Training Logged')]: trainingLogIcon,
  [normalizeAchievementName('Profile Completed')]: profileCompletedIcon,
  [normalizeAchievementName('First Steps')]: firstStepsIcon,
  [normalizeAchievementName('Welcome')]: welcomeIcon,
  [normalizeAchievementName('Anniversary')]: anniversaryIcon,
  [normalizeAchievementName('Apprentice')]: apprenticeIcon,
  [normalizeAchievementName('Trainer')]: trainerIcon,
  [normalizeAchievementName('All-In')]: allInIcon,
};

const getAchievementIconByTypeAndValue = (typeId, timesValue, state) => {
  const key = buildAchievementKey(typeId, timesValue);
  return achievementIconRegistry[key]?.[state] || null;
};

const getAchievementIconByName = (name, state) => {
  const normalizedName = normalizeAchievementName(name);

  if (!normalizedName) {
    return null;
  }

  return achievementIconByName[normalizedName]?.[state] || null;
};

export const getAchievementIconComponent = (
  typeOrAchievement,
  timesValue,
  state = ACHIEVEMENT_ICON_STATES.normal,
) => {
  // Support passing a full achievement object to resolve by name or type/timesValue
  if (typeOrAchievement && typeof typeOrAchievement === 'object') {
    const achievement = typeOrAchievement;
    const resolvedState = typeof timesValue === 'string' ? timesValue : state;

    const matchByName = getAchievementIconByName(achievement.name, resolvedState);
    if (matchByName) {
      return matchByName;
    }

    let valueToUse;
    if (typeof timesValue === 'string') {
      valueToUse = achievement.timesValue;
    } else if (typeof timesValue !== 'undefined') {
      valueToUse = timesValue;
    } else {
      valueToUse = achievement.timesValue;
    }

    return getAchievementIconByTypeAndValue(achievement.type, valueToUse, resolvedState);
  }

  return getAchievementIconByTypeAndValue(typeOrAchievement, timesValue, state);
};

export default achievementIconRegistry;
